import React, { useEffect, useId } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';

import { useAnalyticsPrefix } from '../../AuthContext';
import type { LastUsedMethod } from '../../lastUsedWebAuthMethod';
import { useAccountForm } from '../AccountFormContext';
import { MethodBadgeAnchor } from './MethodBadgeAnchor';

export interface LastUsedAffordanceProps {
  /**
   * Which methods the wrapped control serves. The pill renders when the method
   * resolved onto the form's context is one of them, so a module can wrap every
   * one of its controls and at most one of them draws a badge.
   *
   * Plural because a single control can serve several: the "Continue with email"
   * CTA covers both a password sign-in and a magic link, neither of which has a
   * button of its own.
   */
  methods: readonly LastUsedMethod[];
  /**
   * COMPONENT segment of the `APP-COMPONENT-ACTION` analytics name, supplied by
   * the calling module rather than fixed here, so the pill reports under the
   * same component as the control it describes. The magic-link screens report
   * under `magic-link` where the entry surface reports under `account-form`.
   */
  analyticsComponent: string;
  /**
   * Receives the pill's id, and only when a pill actually renders, for the
   * guarded control to accept as `aria-describedby`.
   *
   * A render prop rather than `cloneElement`: the controls being wrapped are
   * auth-fe components, not DOM elements, so cloning would set the attribute on
   * a React component that has to opt into forwarding it. Making the thread
   * explicit means a control that cannot forward it fails to compile instead of
   * silently dropping the association.
   */
  children: (association: { 'aria-describedby'?: string }) => React.ReactNode;
}

/**
 * Marks the method a returning seller last signed in with, so they can go back
 * to a path that already worked instead of guessing.
 *
 * Reads the resolved method from context rather than taking it as a prop, which
 * is what lets each module own its own badge without a composition threading a
 * renderer down to it — and what keeps this component off the composition's
 * critical path, since adding a badge to a new control is a local change.
 */
export const LastUsedAffordance: React.FC<LastUsedAffordanceProps> = ({
  methods,
  analyticsComponent,
  children,
}) => {
  const { lastUsedMethod, reportBadgeImpression } = useAccountForm();
  const analyticsPrefix = useAnalyticsPrefix();
  const pillId = useId();
  // Narrowed to the matched method rather than a boolean so the effect below
  // depends on a string: `methods` is an array literal at every call site, whose
  // identity changes each render, and depending on it would re-report the badge
  // on every render rather than once per mount.
  const badgedMethod =
    lastUsedMethod !== undefined && methods.includes(lastUsedMethod) ? lastUsedMethod : undefined;

  useEffect(() => {
    if (badgedMethod === undefined) {
      return;
    }
    // Names the method actually badged, not the set this control serves. The form
    // owns the send, and therefore owns dedupe, its ordering, and the telemetry
    // guard — sequencing those correctly is not a thing to trust to a call site.
    reportBadgeImpression(
      `${analyticsPrefix}-${analyticsComponent}-last-used-badge-${badgedMethod}`,
    );
  }, [badgedMethod, analyticsPrefix, analyticsComponent, reportBadgeImpression]);

  if (badgedMethod === undefined) {
    return children({});
  }

  return (
    <MethodBadgeAnchor
      badge={
        // Without the ring the pill disappears into the Apple button, which is
        // the same near-black as the pill itself. `border-tertiary` is white in
        // both themes, since that button's variant doesn't follow the theme.
        <Box
          className="flex h-[20.5px] w-[65px] items-center justify-center rounded-full bg-od-bg-inverse-primary ring-2 ring-od-border-tertiary"
          id={pillId}
        >
          {/*
            Sized directly rather than off the ramp: the atoms body scale stops
            at `body-sm` (14px), which is too heavy for a badge that has to sit
            over a button without crowding its label.

            The box is pinned rather than sized by padding because
            `MethodBadgeAnchor` centers the pill on the button's top border by
            half its own height — so that height has to be a number we chose,
            not whatever the font's line box happens to compute to.
          */}
          <Text as="span" className="whitespace-nowrap text-[11px] text-od-content-inverse-primary">
            Last used
          </Text>
        </Box>
      }
    >
      {children({ 'aria-describedby': pillId })}
    </MethodBadgeAnchor>
  );
};
